<script setup lang="ts">
    import BackButton from '@/components/BackButton.ce.vue';
    import formKitConfig from '@/formkit';
    import { type EventTypeTyping } from '@/typings/widget';
    import { FormKitProvider, defaultConfig } from '@formkit/vue';
    import NumberInput from '@shared/components/form/number-input.vue';
    import RangeSlider from '@shared/components/form/range-slider.vue';
    import WidgetCalendar from '../WidgetCalendar.ce.vue';

    const emit = defineEmits<{(event: 'goBack' | 'next')}>();

    const { t } = useI18n();

    // Stores
    const {
        selectedDate,
        selectedTimeRange,
        guestAmount,
    } = storeToRefs(useWidgetBusinessStore());
    const { venueEventTypes, packageListFilter } = storeToRefs(useWidgetVenueStore());
    const { eventTypeId } = storeToRefs(useWidgetPathStore());

    // Constants
    const MIN = '08:00';
    const MAX = '00:00';

    // Computed
    const selectedEventType: ComputedRef<EventTypeTyping | undefined > = computed(() => venueEventTypes.value.find(eventType => eventType.id === eventTypeId.value));

    // Hooks
    onMounted(() => {
        if (Number(packageListFilter.value.guestAmount) > 0) guestAmount.value = Number(packageListFilter.value.guestAmount);
    });
</script>

<template>
    <div class="date-time-selector">
        <FormKitProvider :config="defaultConfig(formKitConfig)">
            <div class="date-time-selector__header">
                <BackButton
                    :on-click="() => emit('goBack')"
                />
            </div>
            <div class="date-time-selector__title">
                <h2>{{ t('widget.date-time.title', { event: selectedEventType?.name }) }}</h2>
            </div>
            <div class="date-time-selector__content">
                <div class="date-time-selector__date">
                    <WidgetCalendar
                        v-model="selectedDate"
                        :offset-days="2"
                    />
                </div>
                <div class="date-time-selector__time">
                    <div class="date-time-selector__time-content">
                        <RangeSlider
                            v-model="selectedTimeRange"
                            :start-label="t('widget.date-time.start-time')"
                            :end-label="t('widget.date-time.end-time')"
                            :min="MIN"
                            :max="MAX"
                            mode="time"
                        />
                        <NumberInput
                            v-model="guestAmount"
                            :placeholder="`${t('widget.form.guest-amount')}*`"
                            :validation-messages="{
                                required: `${t('widget.form.validations.required', { field: t('widget.form.guest-amount') })}`,
                                min: `${t('widget.form.validations.min', { field: t('widget.form.guest-amount') })}`
                            }"
                            name="guestAmount"
                            validation="number|required|min:1"
                            ignore
                            secondary
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="selectedDate.length && Number(guestAmount) > 0"
                class="date-time-selector__next"
            >
                <button
                    type="button"
                    class="btn btn--primary date-time-selector__next-btn"
                    @click="emit('next')"
                >
                    <span>
                        {{ t('widget.date-time.continue') }}
                    </span>
                </button>
            </div>
        </FormKitProvider>
    </div>
</template>

<style lang="scss" scoped>
.date-time-selector {
    position: relative;
    display: flex;
    flex-direction: column;
    inline-size: 100%;
    min-block-size: 100%;

    &__header {
        line-height: 1;
        margin-block: var(--spacing-04) 0;
        margin-inline: 0;
        padding-block: 0;
        padding-inline: var(--widget-inner-gutter);
    }

    &__title {
        display: flex;
        justify-content: center;

        h2 {
            padding-inline: var(--spacing-03);
            text-align: center;
            text-transform: lowercase;

            @include min-width(md) {
                text-align: start;
            }

            &::first-letter {
                text-transform: uppercase;
            }
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        padding: var(--spacing-03);
        gap: var(--spacing-06);
        min-block-size: 100%;
        padding-block-end: var(--spacing-16);

        @include min-width(lg) {
            flex-direction: row;
            align-items: flex-start;
            padding: var(--spacing-06);

            > * {
                flex: 1;
            }
        }
    }

    &__date,
    &__time {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__time {
        inline-size: 100%;

        &-content {
            display: flex;
            flex-direction: column;
            padding: var(--spacing-04);
            border: 1px solid var(--border-tertiary);
            border-radius: var(--border-radius-md);
            background: rgb(0 0 0 / 0.02);
            gap: var(--spacing-04);
            inline-size: 100%;
            max-inline-size: 400px;
        }
    }

    &__next {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        display: flex;
        inset-block-end: var(--spacing-03);
        inset-inline-start: 50%;
        padding-block: 0 var(--spacing-03);
        padding-inline: var(--spacing-03);
        transform: translateX(-50%);

        @include min-width(md) {
            position: absolute;
            inset-block-end: var(--spacing-04);
            inset-inline: auto var(--spacing-04);
            transform: none;
        }

        &-btn {
            flex: 1;
        }
    }
}
</style>
